import { Controller } from 'react-hook-form';
import { DEFAULT_SYNTHETIC_ASSETS } from '../../../data/returnData';
import { Field } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';
import { type AnyFieldError, toFieldError } from './fieldError';

/**
 * ALLOCATION editor (PLAN.md M7 — the account's static `allocation` side of the allocation /
 * glidePathRef XOR). Edits a `Record<assetClassId, weight>` map: one row per asset class with a
 * weight input, plus add/remove. Weights NEED NOT sum to 1 (the engine normalizes), so we show a
 * live normalized split as a hint rather than forcing the user to hit exactly 100.
 *
 * Asset-class ids are aligned with the offline `DEFAULT_SYNTHETIC_ASSETS` (th_set_tr, global_acwi_tr,
 * th_gov_bond_tr, cash) so a freshly-added account runs offline immediately — but a free-text id is
 * also allowed for plans backed by real series. A `Controller` owns the whole record so adding,
 * renaming, and removing keys stay atomic.
 */

export interface AllocationEditorProps {
  form: SectionProps['form'];
  /** Path to the account's `allocation`, e.g. `accounts.2.allocation`. */
  name: `accounts.${number}.allocation`;
  error?: AnyFieldError;
}

const KNOWN_ASSET_IDS = DEFAULT_SYNTHETIC_ASSETS.map((a) => a.id);

export function AllocationEditor({ form, name, error }: AllocationEditorProps): React.ReactElement {
  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => {
        const record = (field.value as Record<string, number> | undefined) ?? {};
        const entries = Object.entries(record);
        const total = entries.reduce((sum, [, w]) => sum + (Number.isFinite(w) ? w : 0), 0);

        const setEntry = (key: string, weight: number): void => {
          field.onChange({ ...record, [key]: weight });
        };
        const renameEntry = (oldKey: string, newKey: string): void => {
          if (newKey === oldKey) return;
          const next: Record<string, number> = {};
          for (const [k, v] of entries) {
            next[k === oldKey ? newKey : k] = v;
          }
          field.onChange(next);
        };
        const removeEntry = (key: string): void => {
          const next = { ...record };
          delete next[key];
          field.onChange(next);
        };
        const addEntry = (): void => {
          const candidate = KNOWN_ASSET_IDS.find((id) => !(id in record)) ?? '';
          field.onChange({ ...record, [candidate]: entries.length === 0 ? 1 : 0 });
        };

        return (
          <Field
            label="Allocation"
            error={toFieldError(error)}
            hint="Weights normalise — need not sum to 1."
          >
            <div className="alloc">
              {entries.length === 0 && (
                <p className="alloc__empty">No asset weights yet — add one below.</p>
              )}
              {entries.map(([key, weight]) => {
                const share = total > 0 ? weight / total : 0;
                return (
                  <div key={key} className="alloc__row">
                    <input
                      className="input alloc__asset"
                      aria-label="Asset class id"
                      list="alloc-asset-ids"
                      value={key}
                      onBlur={field.onBlur}
                      onChange={(e) => renameEntry(key, e.currentTarget.value)}
                    />
                    <input
                      className="input input--num alloc__weight"
                      type="number"
                      step="0.05"
                      min={0}
                      aria-label={`${key} weight`}
                      value={Number.isNaN(weight) ? '' : weight}
                      onBlur={field.onBlur}
                      onChange={(e) => setEntry(key, e.currentTarget.valueAsNumber)}
                    />
                    <span className="alloc__share num">{(share * 100).toFixed(0)}%</span>
                    <button
                      type="button"
                      className="btn btn--ghost alloc__remove"
                      onClick={() => removeEntry(key)}
                      aria-label={`Remove ${key}`}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              <datalist id="alloc-asset-ids">
                {KNOWN_ASSET_IDS.map((id) => (
                  <option key={id} value={id} />
                ))}
              </datalist>
              <button type="button" className="btn alloc__add" onClick={addEntry}>
                + Add asset class
              </button>
            </div>
          </Field>
        );
      }}
    />
  );
}
